import React, { useRef, useState } from "react";
import DraggablePanel from "./DraggablePanel";
import ScrollableContainer from "../ui/ScrollableContainer";
import FieldLabel from "../ui/FieldLabel";
import { Upload, FolderUp, GripVertical } from "lucide-react";
import { useParsingXml, useProcessingMeshes } from "../../stores/fileStore";
import { useFileLoadingStatus } from "../../stores/panelStore";
import { getUploadMeshIndexEntries } from "../../mesh-editor/uploadIndex";

const FileUploadPanelMenu = ({
  open,
  onClose,
  titleIcon,
  handleFiles,
  loadingPreset = false,
  fileMap = {},
  missingFiles = [],
  uploadingFiles = false,
  mobilePinPanelId,
}) => {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [uploadedFilesHeight, setUploadedFilesHeight] = useState(0.7); // 70% of available space
  const [isResizing, setIsResizing] = useState(false);
  const [isSelectingFiles, setIsSelectingFiles] = useState(false);

  // Get loading progress from panel store
  const { loadingProgress, loadingMessage, loadingPhase } = useFileLoadingStatus();

  // Get file loading states from file store
  const processingMeshes = useProcessingMeshes();
  const parsingXml = useParsingXml();

  // Get phase-specific styling and messages
  const getPhaseInfo = () => {
    switch (loadingPhase) {
      case "xml":
        return { color: "text-blue-400", bgColor: "bg-blue-500", icon: "📄" };
      case "structure":
        return { color: "text-green-400", bgColor: "bg-green-500", icon: "🏗️" };
      case "meshes":
        return { color: "text-yellow-400", bgColor: "bg-yellow-500", icon: "🎨" };
      case "complete":
        return { color: "text-green-400", bgColor: "bg-green-500", icon: "✅" };
      default:
        return { color: "text-blue-400", bgColor: "bg-blue-500", icon: "⏳" };
    }
  };

  const phaseInfo = getPhaseInfo();

  // uploadPositioningMode removed - only XML-based robot loading is supported

  const handleButtonClick = () => {
    if (!uploadingFiles) {
      setIsSelectingFiles(true);
      fileInputRef.current?.click();
    }
  };

  const handleFolderButtonClick = () => {
    if (!uploadingFiles) {
      setIsSelectingFiles(true);
      folderInputRef.current?.click();
    }
  };

  const handleFileSelection = (event) => {
    setIsSelectingFiles(false);
    handleFiles(event);
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Add global mouse event listeners
  React.useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e) => {
        const container = document.querySelector(".file-upload-container");
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const relativeY = (e.clientY - rect.top) / rect.height;

        // Constrain to reasonable bounds (20% to 80%)
        const newHeight = Math.max(0.2, Math.min(0.8, relativeY));
        setUploadedFilesHeight(newHeight);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing]);

  const uploadedFilesCount = Object.keys(fileMap).length;
  const missingFilesCount = missingFiles.length;
  const indexedMeshesCount = getUploadMeshIndexEntries().length;

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="File Upload Viewer"
      titleIcon={titleIcon}
      initialSize={{ width: 340, height: 500 }}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex flex-col h-full w-full p-3 gap-4 text-gray-100 text-sm">
        {/* Loading Progress Indicator */}
        {loadingPreset && (
          <div className="flex-shrink-0 bg-gray-800 rounded-lg p-3 border border-gray-700">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{phaseInfo.icon}</span>
              <span className={`text-sm font-medium ${phaseInfo.color}`}>Loading Robot</span>
            </div>
            {loadingMessage && <div className="text-xs text-gray-300 mb-2">{loadingMessage}</div>}
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className={`${phaseInfo.bgColor} h-2 rounded-full transition-all duration-300 ease-out`}
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
            <FieldLabel className="mt-1">{Math.round(loadingProgress)}%</FieldLabel>
          </div>
        )}

        <div className="flex-shrink-0">
          <label className="block font-semibold mb-1">Choose Files</label>
          <FieldLabel className="mb-2">Mesh index entries: {indexedMeshesCount}</FieldLabel>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xml,.urdf,.xacro,.obj,.stl,.dae,.glb,.gltf,.mtl,.png,.jpg,.jpeg"
            onChange={handleFileSelection}
            className="hidden"
            disabled={uploadingFiles}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            webkitdirectory=""
            onChange={handleFileSelection}
            className="hidden"
            disabled={uploadingFiles}
          />
          <div className="flex gap-2">
            <button
              onClick={handleButtonClick}
              className="menu-button flex-1 flex items-center justify-center gap-2 px-3 py-2 text-gray-100 rounded select-none border border-gray-700 transition-colors duration-150 hover:border-gray-600"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Files</span>
            </button>
            <button
              onClick={handleFolderButtonClick}
              className="menu-button flex-1 flex items-center justify-center gap-2 px-3 py-2 text-gray-100 rounded select-none border border-gray-700 transition-colors duration-150 hover:border-gray-600"
            >
              <FolderUp className="w-4 h-4" />
              <span>Upload Folder</span>
            </button>
          </div>
        </div>

        {/* Positioning options removed - only XML-based robot loading is supported */}

        <div className="flex-1 flex flex-col gap-4 min-h-0 file-upload-container">
          {/* Uploaded Files Section */}
          <div className="flex flex-col min-h-0" style={{ flex: uploadedFilesHeight }}>
            <div className="font-semibold mb-1 flex-shrink-0">
              Uploaded Files <span className="text-gray-400">({uploadedFilesCount})</span>
            </div>

            {/* Initial Upload Progress Circle - Show only when selecting files */}
            {isSelectingFiles && (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2 text-sm text-gray-300">Selecting files...</span>
              </div>
            )}

            {/* Show loading state when files are being processed */}
            {(uploadingFiles || parsingXml) && (
              <div className="text-gray-400 text-xs italic py-2">
                {uploadingFiles ? "Processing uploaded files..." : "Parsing XML file..."}
              </div>
            )}

            {uploadedFilesCount > 0 ? (
              <ScrollableContainer>
                <div className="flex flex-col gap-1 py-1">
                  {Object.keys(fileMap).map((name) => (
                    <div
                      key={name}
                      className="menu-item text-green-300 truncate flex items-center justify-between"
                    >
                      <span className="truncate">{name}</span>
                      {/* Individual File Loading State */}
                      {(isSelectingFiles || uploadingFiles || processingMeshes || parsingXml) && (
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollableContainer>
            ) : !isSelectingFiles && !uploadingFiles && !parsingXml && !processingMeshes ? (
              <div className="panel-text-helper text-xs italic py-2">No files uploaded yet</div>
            ) : null}

            {/* Show loading state when meshes are being processed in the scene */}
            {processingMeshes && uploadedFilesCount > 0 && (
              <div className="text-gray-400 text-xs italic py-2 mt-2">
                Loading meshes into scene...
              </div>
            )}
          </div>

          {/* Resize Handle */}
          {missingFilesCount > 0 && (
            <div
              className="flex-shrink-0 flex items-center justify-center py-2 cursor-row-resize select-none"
              onMouseDown={handleResizeStart}
              style={{ cursor: isResizing ? "row-resize" : "ns-resize" }}
            >
              <div className="w-full h-2 bg-gray-600 rounded-full hover:bg-gray-500 transition-colors duration-150 flex items-center justify-center">
                <GripVertical className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          )}

          {/* Missing Files Section */}
          {missingFilesCount > 0 && (
            <div className="flex flex-col min-h-0" style={{ flex: 1 - uploadedFilesHeight }}>
              <div className="font-semibold text-red-400 mb-1 flex-shrink-0">
                Missing Files <span className="text-red-300">({missingFilesCount})</span>
              </div>
              <ScrollableContainer>
                <div className="flex flex-col gap-1 py-1">
                  {missingFiles.map((name) => (
                    <div key={name} className="menu-item text-red-300 truncate">
                      {name}
                    </div>
                  ))}
                </div>
              </ScrollableContainer>
            </div>
          )}
        </div>
      </div>
    </DraggablePanel>
  );
};

export default FileUploadPanelMenu;
